import type { IReactionDisposer } from 'mobx';
import { reaction } from 'mobx';
import type { IHandlerDescriptor } from '@/components/viewPort/PointerController';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { IViewPortManager } from '../../interfaces';
import type { IIndicationsController } from './Indications.controller';
import type { IIndicationsFactory } from './Indications.factory';
import type { ToolStateStore } from './ToolStateStore';
import { Tools } from './ToolStateStore';
import type { IndicationsService } from './Indications.service';

export interface ICursorSetter {
  setCursor: (cursor: string) => void;
}
export class ToolsManager implements IDisposable {
  private _toolStateStore: ToolStateStore;

  private _indicationsFactory: IIndicationsFactory;

  private _reactionDisposer: IReactionDisposer;

  private _vpms: Array<IViewPortManager>;

  private _indicationsController?: IIndicationsController;

  private _handlersDescriptors: Array<IHandlerDescriptor> = [];

  private _indicationsService: IndicationsService;

  private _cursorSetters: Array<ICursorSetter> = [];

  public constructor(
    toolStateStore: ToolStateStore,
    indicationsFactory: IIndicationsFactory,
    vpms: Array<IViewPortManager>
  ) {
    this._toolStateStore = toolStateStore;
    this._indicationsFactory = indicationsFactory;
    this._vpms = vpms;
    this._indicationsService = this._indicationsFactory.getIndicationService();
    this._indicationsService.setToolManagerDisposeCallback(this.disposeAfterIndicated.bind(this));
    this._reactionDisposer = reaction(() => this._toolStateStore.currentTool, this.onToolChanged.bind(this));
  }

  public onToolChanged(tool: Tools): void {
    this._disposeHandlers();
    this._disposeController();
    if (tool === Tools.NONE || tool === Tools.Delete) {
      return;
    }
    const indicationsController = this._indicationsFactory.createIndicationsController(tool);
    this._vpms.forEach((vpm) => {
      const handler = this._indicationsFactory.createIndicationsPointerHandler(vpm, indicationsController);
      this._cursorSetters.push(handler);
      const descriptor: IHandlerDescriptor = vpm.addPointerHandler(handler);
      this._handlersDescriptors.push(descriptor);
    });
    this._indicationsController = indicationsController;
    this._indicationsController.init();
  }

  private _disposeHandlers(): void {
    this._handlersDescriptors.forEach((desc) => desc.remove());
    this._handlersDescriptors = [];
  }

  private _disposeCursors(): void {
    this._cursorSetters.forEach((setter) => setter.setCursor(''));
    this._cursorSetters = [];
  }

  private _disposeController(): void {
    this._indicationsController?.dispose();
    this._indicationsController = undefined;
  }

  public disposeAfterIndicated(): void {
    this._disposeCursors();
    this._disposeHandlers();
    this._disposeController();
    this._toolStateStore.setCurrentTool(Tools.NONE);
  }

  public dispose(): void {
    this._disposeCursors();
    this._reactionDisposer();
    this._disposeHandlers();
    this._disposeController();
    this._toolStateStore.setCurrentTool(Tools.NONE);
  }
}
